import { PlanItem } from "@dto/PlanItem";
import { planItemComparer } from "@store/plan/planReducer";
import { StartPoint } from "@domain/StartPoint";
import { ItemDirection } from "@domain/ItemDirection";
import { RouteLeg, RouteTravelMode } from "./@google/types";
import { requestDirections } from "./directionsApi";
import { t } from "i18next";

/**
 * Calculate travel for plan items and start points with start points.
 * TODO: This function will only work for plan items and start points with dayIndex
 * assigned in an expected manner, eg: if you want to get directions for a random
 * set of plan items and perhaps a start point, ensure dayIndex is null for all and plan items
 * have position assigned.
 */
const getDirections = async (
  planItems: PlanItem[],
  startPoints: StartPoint[],
  travelMode: RouteTravelMode,
  // For all time, the directions have used lat/lng of entity. Allow using address where it
  // is necessary like Self Guided tours.
  useAddress = false,
): Promise<ItemDirection[]> => {
  const merged = mergePlanItemsAndStartPoints(planItems, startPoints);
  const batches = getDirectionRequestBatches(merged, useAddress);

  let directions: ItemDirection[];
  if (merged.length > 0 && batches.length === 0) {
    // 1 or more items but all share same location and thus no directions necessary.
    const location = getLocation(merged[0]);
    const locationKey = getWaypointString(merged[0]);
    directions = [
      {
        distanceInMeters: 0,
        overviewPolyline: [],
        steps: [],
        timeInSeconds: 0,
        start: {
          items: merged,
          location: location,
          directionsLocation: location,
          locationKey: locationKey,
        },
        end: {
          items: merged,
          location: location,
          directionsLocation: location,
          locationKey: locationKey,
        },
      },
    ];
  } else {
    directions = await getDirectionsMappedToPlanItems(batches, travelMode);
  }
  return directions;
};

const mergePlanItemsAndStartPoints = (
  planItems: PlanItem[],
  startPoints: StartPoint[],
) => {
  const sorted = [...planItems].sort(planItemComparer);
  let dayIndex = -1 as number | null;
  const merged = [] as (PlanItem | StartPoint)[];

  for (let i = 0; i < sorted.length; ++i) {
    const planItem = sorted[i];
    if (planItem.dayIndex !== dayIndex) {
      dayIndex = planItem.dayIndex;

      const start = startPoints.find((x) => x.dayIndex === dayIndex);
      if (start) {
        merged.push(start);
      }
    }

    const latitude = planItem?.plannable?.location?.latitude ?? null;
    const longitude = planItem?.plannable?.location?.longitude ?? null;

    if (latitude !== null && longitude !== null) {
      merged.push(planItem);
    }
  }

  return merged;
};

// TODO: can we remove this?
const getWaypointString = (item: PlanItem | StartPoint) => {
  const location = getLocation(item);
  return `${location.latitude},${location.longitude}`;
};

const getWaypointAddressString = (item: PlanItem | StartPoint) => {
  if (item.type === "StartPoint" && !item.location.address) {
    return getWaypointString(item);
  }
  const location = getLocation(item);
  return location.address!;
};

const getItems = (items: (StartPoint | PlanItem)[], start: number) => {
  const group = [items[start]];

  let i = start + 1;
  for (; i < items.length; ++i) {
    const last = items[i - 1];
    const current = items[i];
    if (getWaypointString(last) === getWaypointString(current)) {
      group.push(current);
    } else {
      i -= 1;
      break;
    }
  }

  return {
    nextStart: i,
    group: group,
  };
};

const getDirectionRequestBatches = (
  items: (PlanItem | StartPoint)[],
  useAddress: boolean,
) => {
  const requests = [] as {
    start: {
      items: (StartPoint | PlanItem)[];
      value: string;
      valueType: "address" | "location";
    };
    end?: {
      items: (StartPoint | PlanItem)[];
      value: string;
      valueType: "address" | "location";
    };
    waypoints: {
      items: (StartPoint | PlanItem)[];
      value: string;
      valueType: "address" | "location";
    }[];
  }[];

  // short circuit if no items
  if (items.length === 0) {
    return requests;
  }

  const groupedByLocation = [] as (PlanItem | StartPoint)[][];

  for (let i = 0; i < items.length; ++i) {
    const { nextStart, group } = getItems(items, i);
    i = nextStart;
    groupedByLocation.push(group);
  }

  // need at least two different locations to get directions
  if (groupedByLocation.length === 1) {
    return requests;
  }

  requests.push({
    start: {
      items: groupedByLocation[0],
      value: useAddress
        ? getWaypointAddressString(groupedByLocation[0][0])
        : getWaypointString(groupedByLocation[0][0]),
      valueType: useAddress ? "address" : "location",
    },
    waypoints: [],
  });

  for (let i = 1; i < groupedByLocation.length; ++i) {
    const items = groupedByLocation[i];
    if (i % 23 === 0 && i + 1 < groupedByLocation.length) {
      // there is a cap of 25 waypoints plus origin and end
      requests[requests.length - 1].end = {
        items: items,
        value: useAddress
          ? getWaypointAddressString(items[0])
          : getWaypointString(items[0]),
        valueType: useAddress ? "address" : "location",
      };
      requests.push({
        start: {
          items: items,
          value: useAddress
            ? getWaypointAddressString(items[0])
            : getWaypointString(items[0]),
          valueType: useAddress ? "address" : "location",
        },
        waypoints: [],
      });
    } else if (i === groupedByLocation.length - 1) {
      requests[requests.length - 1].end = {
        items: items,
        value: useAddress
          ? getWaypointAddressString(items[0])
          : getWaypointString(items[0]),
        valueType: useAddress ? "address" : "location",
      };
    } else {
      requests[requests.length - 1].waypoints.push({
        items: items,
        value: useAddress
          ? getWaypointAddressString(items[0])
          : getWaypointString(items[0]),
        valueType: useAddress ? "address" : "location",
      });
    }
  }

  return requests;
};

export interface ItemDirectionStepItems {
  items: (PlanItem | StartPoint)[];
  locationKey: string;
  /**
   * Location summary from position of our entities.
   */
  location: {
    address?: string;
    longitude: number;
    latitude: number;
  };
  /**
   * Location directions are for.
   * Our location is often the center of a building while directions will be some
   * location on the street.
   */
  directionsLocation: {
    longitude: number;
    latitude: number;
  };
}

const getLocation = (item: PlanItem | StartPoint) => {
  return item?.type === "StartPoint"
    ? item.location
    : {
        address: item.plannable.location?.address ?? "",
        latitude: item.plannable.location?.latitude ?? 0,
        longitude: item.plannable.location?.longitude ?? 0,
      };
};

const durationStringToSeconds = (duration: string): number => {
  return parseInt(duration.replace("s", ""), 10);
};

export const getDirectionsMappedToPlanItems = async (
  batches: {
    start: {
      items: (StartPoint | PlanItem)[];
      value: string;
      valueType: "address" | "location";
    };
    end?: {
      items: (StartPoint | PlanItem)[];
      value: string;
      valueType: "address" | "location";
    };
    waypoints: {
      items: (StartPoint | PlanItem)[];
      value: string;
      valueType: "address" | "location";
    }[];
  }[],
  travelMode: RouteTravelMode,
) => {
  const directionRequests = batches.map((batch) => {
    // TODO: for legacy reasons, this is an overly unnecessary mapping of properties to what the api
    // request needs. Look at storing the latitude/longitude/address as an object closer to what
    // is being mapped to below.
    return requestDirections({
      travelMode,
      origin: {
        address:
          batch.start.valueType === "address" ? batch.start.value : undefined,
        location:
          batch.start.valueType === "location"
            ? {
                latLng: {
                  latitude: Number(batch.start.value.split(",")[0]),
                  longitude: Number(batch.start.value.split(",")[1]),
                },
              }
            : undefined,
      },
      destination: {
        address:
          batch.end!.valueType === "address" ? batch.end!.value : undefined,
        location:
          batch.end!.valueType === "location"
            ? {
                latLng: {
                  latitude: Number(batch.end!.value.split(",")[0]),
                  longitude: Number(batch.end!.value.split(",")[1]),
                },
              }
            : undefined,
      },
      waypoints: batch.waypoints.map((x) => ({
        address: x.valueType === "address" ? x.value : undefined,
        location:
          x.valueType === "location"
            ? {
                latLng: {
                  latitude: Number(x.value.split(",")[0]),
                  longitude: Number(x.value.split(",")[1]),
                },
              }
            : undefined,
      })),
    }).then((directionsResponse) => {
      return {
        directionsResponse,
        batch,
      };
    });
  });

  const directionsBatches = await Promise.all(directionRequests);
  const planItemTravelSummary = {} as {
    [planItemOrStartPointId: number | string]: {
      distanceInMeters: number;
      timeInSeconds: number;
    };
  };

  const directions = [] as ItemDirection[];

  const legsMappedToItems = directionsBatches.reduce(
    (aggregate, current) => {
      const legs = current.directionsResponse?.routes[0]?.legs ?? [];
      const legsAndItems = legs.map((leg, index) => {
        return {
          leg: leg,
          endItems:
            index === legs.length - 1
              ? current.batch.end?.items
              : current.batch.waypoints[index]?.items,
          startItems:
            index === 0
              ? current.batch.start.items
              : current.batch.waypoints[index - 1]?.items,
        };
      });
      aggregate = aggregate.concat(legsAndItems);
      return aggregate;
    },
    [] as {
      leg: RouteLeg;
      endItems: (StartPoint | PlanItem)[] | undefined;
      startItems: (StartPoint | PlanItem)[];
    }[],
  );

  legsMappedToItems.forEach((legItem) => {
    const leg = legItem.leg;
    const value = {
      timeInSeconds: durationStringToSeconds(leg.duration),
      distanceInMeters: leg.distanceMeters,
    };

    planItemTravelSummary[legItem.endItems![0]!.id] = value;

    directions.push({
      distanceInMeters: leg.distanceMeters,
      steps: leg.steps.map((step) => {
        return {
          distanceInMeters: step.distanceMeters,
          instructions:
            step.navigationInstruction?.instructions ?? t("common.continue"),
          timeInSeconds: durationStringToSeconds(step.staticDuration),
          manuever: step.navigationInstruction?.maneuver,
        };
      }),
      timeInSeconds: durationStringToSeconds(leg.duration),
      start: {
        locationKey: getWaypointString(legItem.startItems[0]),
        location: getLocation(legItem.startItems[0]),
        directionsLocation: legItem.leg.startLocation.latLng,
        items: legItem.startItems,
      },
      end: {
        locationKey: getWaypointString(legItem.endItems![0]),
        location: getLocation(legItem.endItems![0]),
        directionsLocation: legItem.leg.endLocation.latLng,
        items: legItem.endItems!,
      },
      overviewPolyline: leg.steps.reduce(
        (aggregate, current) => {
          if (current.polyline?.geoJsonLinestring) {
            aggregate = aggregate.concat(
              current.polyline.geoJsonLinestring.coordinates.map((point) => {
                return {
                  latitude: point[1],
                  longitude: point[0],
                };
              }),
            );
          }

          return aggregate;
        },
        [] as { latitude: number; longitude: number }[],
      ),
    });
  });

  return directions;
};

export { getDirections, mergePlanItemsAndStartPoints, getLocation };
